/**
 * Theme selection and color math. See docs/ui-design.md §6 and ADR-0019.
 *
 * Keep THEME_STORAGE_KEY and the resolve logic in sync with the pre-paint
 * script in index.html (src/ui/theme.test.ts checks that they agree).
 */

export type ThemeMode = "system" | "light" | "dark";
export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "afterimage:theme";

export const parseThemeMode = (stored: string | null): ThemeMode =>
  stored === "light" || stored === "dark" ? stored : "system";

export const resolveTheme = (mode: ThemeMode, prefersDark: boolean): Theme =>
  mode === "system" ? (prefersDark ? "dark" : "light") : mode;

/**
 * The header toggle (ADR-0027) flips between light and dark. If the new theme
 * matches the OS preference, it returns to `system` instead of pinning, so
 * the app keeps following the OS until the user picks the other theme.
 */
export function toggleTheme(mode: ThemeMode, prefersDark: boolean): ThemeMode {
  const next: Theme = resolveTheme(mode, prefersDark) === "dark" ? "light" : "dark";
  return next === resolveTheme("system", prefersDark) ? "system" : next;
}

function parseHex(color: string): [number, number, number] {
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(color);
  const full = short
    ? `${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`
    : /^#([0-9a-f]{6})$/i.exec(color)?.[1];
  if (full === undefined) throw new Error(`Not a hex color: ${color}`);
  const n = Number.parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function relativeLuminance(color: string): number {
  const [r, g, b] = parseHex(color).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.x contrast ratio between two hex colors (1–21). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [
    number,
    number,
  ];
  return (hi + 0.05) / (lo + 0.05);
}
